import type { TimelineEventKind } from '../api/types'
import type { DataItem } from './rows'

/**
 * Which parts of the report belong to the paid tier — and, for now, nothing more than that.
 *
 * **Nothing here locks anything.** Every reader gets the whole document; this module only marks the
 * parts that would be paid for, so the report can show what a premium report contains while it is
 * being given away. That is deliberate: the point of the free period is to learn what people value
 * before there is a paywall to argue with, which needs the premium content visible rather than
 * teased. When payments do arrive, this list is where the two tiers get told apart, and the UI that
 * reads it will need a second state for "not paid" that does not exist yet.
 *
 * The split is inherited rather than invented — it is what the previous version of this product
 * sold, minus two things this API does not serve at all: whether the vehicle is stolen, and its
 * current market value.
 *
 * "The repairs a model commonly needs" is in the paid tier as `modelgebreken`, served by the API's
 * `/defect-profile`: it is the one thing the old product charged for that this API can also answer,
 * and it is genuinely expensive to produce, since a cache miss costs the API a dozen RDW requests.
 *
 * One item does not fall on a section boundary. The APK expiry date is free — it is the first
 * thing anybody checks — and the findings behind it are not, so the APK card is free and the
 * history inside it is marked, rather than the whole card going premium.
 *
 * The road tax is the same shape, and the one premium item this product adds rather than inherits.
 * The `kosten` card is free: its energy estimate is priced from figures anyone can look up, and its
 * exemption dates follow from one public rule. The per-province road tax inside it is a
 * `PremiumBlock`, because an amount for this plate in every province is published nowhere else.
 */

/** What the premium report will cost. Quoted in the banner; nothing charges it yet. */
export const PREMIUM_PRICE_EUR = 3.99

/** Sections whose whole card is premium, by `SectionSpec.id`. */
const PREMIUM_SECTION_IDS: ReadonlySet<string> = new Set([
  'tellerstand',
  'terugroepacties',
  'objecten',
  'zeldzaamheid',
  // What this model usually gets pulled up on at the APK. See the note above: the one feature the
  // old product sold that this API can also answer.
  'modelgebreken',
  // Battery and charging. The only premium card whose data is licensed from someone else. CC BY-SA
  // permits commercial use outright, so charging for it is fine — but attribution has to reach
  // whoever actually sees the figures, so if this ever becomes a real paywall rather than a label,
  // the source note goes behind it together with the numbers, never in front of them.
  'accu',
])

/**
 * The kinds of timeline event that belong to the paid tier.
 *
 * **The timeline card itself is free, and the split runs per entry** — the same shape as the APK
 * card, which is free with its history marked inside it. Three of the four lists the timeline merges
 * are paid content, so marking the whole card would be defensible, but it also carries the
 * registration dates, which are free, and a timeline is the one place a free reader can see the
 * shape of a car's life at a glance. That is worth giving away.
 *
 * The keuringen, the recalls and the retrofits carry the mark; the toelating dates, the
 * tenaamstelling and the APK expiry do not.
 *
 * These are the API's `TimelineEventKind` values, PascalCase as they arrive on the wire — and
 * unlike the row and tile lists below, this one is matched on the contract rather than on a Dutch
 * label, so renaming a heading cannot silently unmark it.
 */
const PREMIUM_TIMELINE_KINDS: ReadonlySet<TimelineEventKind> = new Set([
  'Inspection',
  'RecallAnnounced',
  'EquipmentFitted',
  'EquipmentRemoved',
])

export function isPremiumTimelineEvent(kind: TimelineEventKind): boolean {
  return PREMIUM_TIMELINE_KINDS.has(kind)
}

/**
 * Individual premium rows inside otherwise free sections, matched on their Dutch label.
 *
 * Matching on the label is the weak point of this file: renaming a row in `ReportView` silently
 * unmarks it, and nothing fails. Change the two together.
 */
const PREMIUM_ROW_LABELS: ReadonlySet<string> = new Set([
  'Elektrisch verbruik',
  'Actieradius elektrisch WLTP',
  'Actieradius elektrisch stad WLTP',
])

/**
 * Quick-check tiles that summarise premium content, by their Dutch label.
 *
 * The tiles are the first thing anybody reads, so this is where the marking earns its keep: it
 * shows what the paid report answers before the reader has scrolled to any of it. The tile is
 * marked whatever the answer turns out to be — "Terugroepacties: Geen" is the premium check
 * returning nothing, not a free one.
 */
const PREMIUM_TILE_LABELS: ReadonlySet<string> = new Set([
  'Tellerstand',
  'Terugroepacties',
  'APK-bevindingen',
])

export function isPremiumSection(id: string): boolean {
  return PREMIUM_SECTION_IDS.has(id)
}

export function isPremiumTile(label: string): boolean {
  return PREMIUM_TILE_LABELS.has(label)
}

/** Flags the premium rows in a list built by `rows()`, leaving the rest untouched. */
export function markPremium(items: DataItem[]): DataItem[] {
  return items.map((item) => (PREMIUM_ROW_LABELS.has(item.label) ? { ...item, premium: true } : item))
}
